/*
 * KzmCal — vanilla JS date-range picker with client-derived min-stay,
 * async availability, inseparable blocks, and a busy-lock shared with the site.
 *
 * See /workspace/calendar/PLAN2.md for the full design. Public API at the bottom.
 */
(function (global) {
  'use strict';

  /* ========================================================================
   * CalDate — local-midnight date primitive
   * ====================================================================== */
  class CalDate {
    constructor(y, m, d) { this._d = new Date(y, m, d); }

    static today() {
      const n = new Date();
      return new CalDate(n.getFullYear(), n.getMonth(), n.getDate());
    }

    static fromISO(v) {
      if (v == null) return null;
      if (v instanceof CalDate) return v.clone();
      if (v instanceof Date) return new CalDate(v.getFullYear(), v.getMonth(), v.getDate());
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
      if (!m) return null;
      return new CalDate(+m[1], +m[2] - 1, +m[3]);
    }

    static fromTime(t) {
      const d = new Date(+t);
      return new CalDate(d.getFullYear(), d.getMonth(), d.getDate());
    }

    clone() { return new CalDate(this.year(), this.month(), this.day()); }
    year()  { return this._d.getFullYear(); }
    month() { return this._d.getMonth(); }
    day()   { return this._d.getDate(); }
    dow()   { return this._d.getDay(); }
    time()  { return this._d.getTime(); }
    toJS()  { return new Date(this._d.getTime()); }

    addDays(n)   { return new CalDate(this.year(), this.month(), this.day() + n); }
    addMonths(n) { return new CalDate(this.year(), this.month() + n, this.day()); }
    startOfMonth() { return new CalDate(this.year(), this.month(), 1); }

    isBefore(o) { return this._d < o._d; }
    isAfter(o)  { return this._d > o._d; }
    isSame(o)   { return o && this.year() === o.year() && this.month() === o.month() && this.day() === o.day(); }
    isBetween(a, b, inclusive) {
      if (inclusive === false) return this.isAfter(a) && this.isBefore(b);
      return !this.isBefore(a) && !this.isAfter(b);
    }

    diffDays(o) {
      return Math.round((this._d.getTime() - o._d.getTime()) / 86400000);
    }

    toISOLocal() {
      const y = this.year();
      const m = String(this.month() + 1).padStart(2, '0');
      const d = String(this.day()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }

  /* ========================================================================
   * Emitter
   * ====================================================================== */
  class Emitter {
    constructor() { this._listeners = new Map(); }
    on(event, fn) {
      if (typeof fn !== 'function') return;
      if (!this._listeners.has(event)) this._listeners.set(event, new Set());
      this._listeners.get(event).add(fn);
    }
    off(event, fn) {
      const set = this._listeners.get(event);
      if (set) set.delete(fn);
    }
    _emit(event, detail) {
      const set = this._listeners.get(event);
      if (!set) return;
      for (const fn of Array.from(set)) {
        try { fn(detail); }
        catch (e) { console.error(`KzmCal handler for "${event}" threw:`, e); }
      }
    }
  }

  /* ========================================================================
   * BusyLock — reference-counted loading state with a root-class toggle
   * ====================================================================== */
  class BusyLock {
    constructor(emitter, setClass) {
      this._emitter = emitter;
      this._setClass = setClass;
      this._counts = new Map();
      this._total = 0;
    }

    count() { return this._total; }

    beginBusy(key) {
      const k = key || '_default';
      this._counts.set(k, (this._counts.get(k) || 0) + 1);
      const prev = this._total;
      this._total += 1;
      if (prev === 0) {
        this._setClass(true);
        this._emitter._emit('busy', { count: this._total });
      }
    }

    endBusy(key) {
      const k = key || '_default';
      const c = this._counts.get(k) || 0;
      if (c <= 0) return;
      const next = c - 1;
      if (next === 0) this._counts.delete(k); else this._counts.set(k, next);
      this._total -= 1;
      if (this._total === 0) {
        this._setClass(false);
        this._emitter._emit('idle', {});
      }
    }

    holdBusy(pOrFn, key) {
      const k = key || '_hold';
      const p = typeof pOrFn === 'function' ? pOrFn() : pOrFn;
      const settled = Promise.resolve(p);
      this.beginBusy(k);
      settled.finally(() => this.endBusy(k));
      return settled;
    }
  }

  /* ========================================================================
   * Validator — blocked-reason computation, preview clamping
   * ====================================================================== */
  const BlockedReason = {
    PAST: 'past',
    OCCUPIED: 'occupied',
    // A booked night with a free night before it: no arrival, but the guest
    // already in the flat may still leave that morning.
    DEPARTURE_ONLY: 'departure-only',
    MIN_STAY: 'min-stay',
    // The mirror of MIN_STAY: a check-out so far out that the stay would run
    // past `maxNights`.
    MAX_STAY: 'max-stay',
    OUT_OF_BOUNDS: 'out-of-bounds',
    SOFT: 'soft',
  };

  function nightBlocked(night, occupied) {
    for (const r of occupied) {
      if (night.isBetween(r.from, r.to)) return true;
    }
    return false;
  }

  function cellReason(date, ctx) {
    const { today, minDate, maxDate, occupied, minNights, maxNights, startDate, selectMode, userFilter } = ctx;

    if (userFilter) {
      const uf = userFilter(date, { mode: selectMode, startDate });
      if (uf === 'hard' || uf === true) return BlockedReason.OUT_OF_BOUNDS;
      if (uf === 'soft') return BlockedReason.SOFT;
    }

    if (date.isBefore(today)) return BlockedReason.PAST;
    if (minDate && date.isBefore(minDate)) return BlockedReason.OUT_OF_BOUNDS;
    if (maxDate && date.isAfter(maxDate)) return BlockedReason.OUT_OF_BOUNDS;

    /*
     * `occupied` holds blocked *nights*, and a night belongs to the day it
     * starts on. A booked night therefore rules its day out as a check-in, but
     * says nothing about it as a check-out: the guest leaving that morning
     * slept the night *before*, so the day a booking starts is a perfectly
     * legal departure for whoever is already in the flat. A check-out is
     * judged by the night before it — the last one actually slept.
     */
    const asCheckOut = selectMode === 'to' && startDate && date.isAfter(startDate);
    const lastNight = asCheckOut ? date.addDays(-1) : date;
    if (nightBlocked(lastNight, occupied)) {
      // Judging an arrival on a booked night: if the night before it is free
      // the day is the turnover — no arrival, but a legal departure, and it
      // should not look like the dead days in the middle of the booking. Only
      // a day booked on both sides is unusable in every role.
      return (!asCheckOut && !nightBlocked(date.addDays(-1), occupied))
        ? BlockedReason.DEPARTURE_ONLY
        : BlockedReason.OCCUPIED;
    }

    if (selectMode === 'to' && startDate && !date.isBefore(startDate)) {
      // The start cell itself is never blocked — clicking it re-picks the start.
      if (date.isSame(startDate)) return null;
      if (date.diffDays(startDate) < minNights) return BlockedReason.MIN_STAY;
      /*
       * Checked before the crossing test below: a day a year out nearly always
       * has a booking somewhere in between, and "Min. stay" on a day that is in
       * truth too *far* away explains the wrong thing.
       */
      if (maxNights && date.diffDays(startDate) > maxNights) return BlockedReason.MAX_STAY;
      // A block the range would have to cross. One starting *on* `date` does
      // not count: that night is not part of the stay.
      for (const r of occupied) {
        if (startDate.isBefore(r.from) && r.from.isBefore(date)) return BlockedReason.MIN_STAY;
      }
      return null;
    }

    // from-mode — and, in to-mode, every date before the start, which is judged
    // as a candidate *new* start so the user can move the check-in backwards.
    // The stay occupies `date … minTo-1`; `minTo` is the check-out and may fall
    // on the day the next booking starts.
    const minTo = date.addDays(minNights);
    for (const r of occupied) {
      if (date.isBefore(r.from) && r.from.isBefore(minTo)) return BlockedReason.MIN_STAY;
    }
    return null;
  }

  function isHardReason(r) {
    return r === BlockedReason.PAST ||
           r === BlockedReason.OCCUPIED ||
           r === BlockedReason.DEPARTURE_ONLY ||
           r === BlockedReason.MIN_STAY ||
           r === BlockedReason.MAX_STAY ||
           r === BlockedReason.OUT_OF_BOUNDS;
  }

  function previewEnd(S, H, occupied, minNights, maxNights) {
    if (!S || !H) return null;
    if (H.isBefore(S) || H.isSame(S)) return null;
    let end = H;
    let nearest = null;
    for (const r of occupied) {
      if (S.isBefore(r.from) && r.from.isBefore(H)) {
        if (!nearest || r.from.isBefore(nearest)) nearest = r.from;
      }
    }
    // Clamp *to* the day the next booking starts, not the day before it — that
    // day is the last legal check-out, the turnover.
    if (nearest) end = nearest;
    // Same reasoning as the booking clamp: the days past the ceiling cannot be
    // picked, so the band stops at the last check-out that can. Applied after
    // the booking clamp so the *earlier* of the two limits wins.
    if (maxNights && end.diffDays(S) > maxNights) end = S.addDays(maxNights);
    if (!end.isAfter(S)) return null;
    // Cells between S and S + minNights are hard-blocked; never preview a
    // range the user cannot actually select.
    if (minNights > 0 && end.diffDays(S) < minNights) return null;
    return end;
  }

  function firstAvailableEnd(S, minNights, occupied, maxDate) {
    if (!S) return null;
    const candidate = S.addDays(minNights);
    if (maxDate && candidate.isAfter(maxDate)) return null;
    // If this candidate is inside or past a block adjacent to S, push past or give up.
    let nearest = null;
    for (const r of occupied) {
      if (S.isBefore(r.from)) {
        if (!nearest || r.from.isBefore(nearest)) nearest = r.from;
      }
    }
    // `candidate` landing exactly on `nearest` is the tightest legal stay:
    // the last night is `nearest - 1` and the next guest arrives as this one
    // leaves. Only a candidate *past* it would have to cross the block.
    if (nearest && candidate.isAfter(nearest)) return null;
    return candidate;
  }

  /* ========================================================================
   * AvailabilityLoader — fetch coalescing + stale-response token
   * ====================================================================== */
  class AvailabilityLoader {
    constructor({ fn, busyLock, emitter }) {
      this._fn = fn;
      this._busy = busyLock;
      this._emitter = emitter;
      this._ranges = [];
      this._loaded = null;
      // Generation counter. Bumped only by a *replacing* refresh, so an
      // additive fetch is never discarded merely because a later request for a
      // different window was issued after it.
      this._gen = 0;
      this._inflight = [];
    }

    get ranges() { return this._ranges; }
    get loadedWindow() { return this._loaded; }

    /** Loaded window ∪ every in-flight request, or null when nothing is known. */
    requestedWindow() {
      let from = this._loaded ? this._loaded.from : null;
      let to = this._loaded ? this._loaded.to : null;
      for (const p of this._inflight) {
        if (!from || p.from.isBefore(from)) from = p.from;
        if (!to || p.to.isAfter(to)) to = p.to;
      }
      return (from && to) ? { from, to } : null;
    }

    async fetch(from, to, opts) {
      if (!this._fn) return;
      const replace = !!(opts && opts.replace);
      if (replace) this._gen += 1;
      const gen = this._gen;
      const pending = { from: from.clone(), to: to.clone() };
      this._inflight.push(pending);
      this._busy.beginBusy('availability');
      try {
        const raw = await this._fn(from.toISOLocal(), to.toISOLocal());
        if (gen !== this._gen) return;
        const parsed = (raw || [])
          .map(r => ({ from: CalDate.fromISO(r.from), to: CalDate.fromISO(r.to) }))
          .filter(r => r.from && r.to && !r.to.isBefore(r.from));
        // Swap only once the new data is in hand — a failed refresh must leave
        // the previous cache untouched.
        if (replace) {
          this._ranges.length = 0;
          this._loaded = null;
        }
        this._mergeRanges(parsed);
        this._growWindow(from, to);
        this._emitter._emit('availability:loaded', {
          from: from.toISOLocal(),
          to: to.toISOLocal(),
          ranges: this._ranges.slice(),
        });
      } catch (error) {
        if (gen !== this._gen) return;
        this._emitter._emit('availability:error', { error });
      } finally {
        const i = this._inflight.indexOf(pending);
        if (i !== -1) this._inflight.splice(i, 1);
        this._busy.endBusy('availability');
      }
    }

    refresh() {
      if (!this._loaded) return;
      const { from, to } = this._loaded;
      return this.fetch(from.clone(), to.clone(), { replace: true });
    }

    _mergeRanges(newRanges) {
      const key = r => `${r.from.toISOLocal()}|${r.to.toISOLocal()}`;
      const seen = new Set(this._ranges.map(key));
      for (const r of newRanges) {
        if (!seen.has(key(r))) {
          this._ranges.push(r);
          seen.add(key(r));
        }
      }
      this._ranges.sort((a, b) => a.from.time() - b.from.time());
    }

    _growWindow(from, to) {
      if (!this._loaded) {
        this._loaded = { from: from.clone(), to: to.clone() };
        return;
      }
      if (from.isBefore(this._loaded.from)) this._loaded.from = from.clone();
      if (to.isAfter(this._loaded.to)) this._loaded.to = to.clone();
    }

    maybeExtendForVisible(visStart, visEnd) {
      if (!this._fn) return;
      const win = this.requestedWindow();
      if (!win) {
        // Nothing cached and nothing in flight — the initial load must have
        // failed. Navigating is a retry opportunity, so take it.
        this.fetch(visStart.addMonths(-1), visEnd.addMonths(3));
        return;
      }
      const edgeMargin = 1;
      // Normalised to month starts: the loaded window begins at *today*, not at
      // a month boundary, which used to place the very first "next" click
      // inside the left margin and fire a pointless backwards fetch.
      const leftEdge = win.from.startOfMonth().addMonths(edgeMargin);
      const rightEdge = win.to.startOfMonth().addMonths(-edgeMargin);
      if (visStart.isBefore(leftEdge)) {
        const newFrom = visStart.addMonths(-3);
        const newTo = win.from.addDays(-1);
        // A window that ends before today can only contain past dates, which
        // are blocked regardless of availability.
        if (newFrom.isBefore(newTo) && !newTo.isBefore(CalDate.today())) {
          this.fetch(newFrom, newTo);
        }
      }
      if (visEnd.isAfter(rightEdge)) {
        const newFrom = win.to.addDays(1);
        const newTo = visEnd.addMonths(3);
        if (newFrom.isBefore(newTo)) this.fetch(newFrom, newTo);
      }
    }
  }

  /* ========================================================================
   * Renderer
   * ====================================================================== */
  class Renderer {
    constructor(root, options, state, emitter) {
      this._root = root;
      this._options = options;
      this._state = state;
      this._emitter = emitter;
      this._monthFmt = new Intl.DateTimeFormat(options.locale, { month: 'long', year: 'numeric' });
      this._weekdayFmt = new Intl.DateTimeFormat(options.locale, { weekday: 'short' });
      this._ariaFmt = new Intl.DateTimeFormat(options.locale, {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      });
      this._dayNames = this._computeDayNames(options.firstDay);
      this._container = null;
      this._wrapper = null;
      this._focusTime = null;
      this._tooltipEl = null;
      this._tooltipTpl = (typeof options.rangeTooltip === 'object' && options.rangeTooltip &&
                          options.rangeTooltip.template)
        ? options.rangeTooltip.template
        : defaultTooltipTemplate(options);
    }

    _computeDayNames(firstDay) {
      const base = new Date(1970, 0, 4); // Jan 4, 1970 is a Sunday
      const names = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + ((firstDay + i) % 7));
        names.push(this._weekdayFmt.format(d));
      }
      return names;
    }

    get container() { return this._container; }

    mount() {
      this._root.innerHTML = '';
      const container = document.createElement('div');
      // `kzm-cal` is fixed and is what KzmCal.css keys off; the second class is
      // the themeable/legacy hook. A host whose own markup already uses
      // `js-easypicker-container` (kurzzeitmiete.at does) overrides it via
      // `containerClass` so the two never collide.
      container.className = 'kzm-cal ' + this._options.containerClass;

      const wrapper = document.createElement('div');
      wrapper.className = 'js-easypicker-wrapper';
      wrapper.style.setProperty('--ep-cals', String(this._options.calendars));
      container.appendChild(wrapper);

      if (this._options.popup) {
        const popup = document.createElement('div');
        popup.className = 'ep-popup';
        popup.hidden = true;
        container.appendChild(popup);
      }

      // Sibling of the wrapper, like .ep-popup, so renderGrid()'s innerHTML
      // reset cannot destroy it.
      if (this._options.rangeTooltip) {
        const tip = document.createElement('div');
        tip.className = 'ep-tooltip';
        tip.hidden = true;
        container.appendChild(tip);
        this._tooltipEl = tip;
      }

      // Sibling of the wrapper, not a child of the last month, so it centres
      // across the whole card the way the live footer does.
      if (this._options.footerInfoHTML) {
        // div, not <footer>, for the same reason as .month-header — and the
        // site this mirrors already marks its own footer info up as a div.
        const footer = document.createElement('div');
        footer.className = 'footer-info';
        footer.innerHTML = this._options.footerInfoHTML;
        container.appendChild(footer);
      }

      this._root.appendChild(container);
      this._container = container;
      this._wrapper = wrapper;
      this.renderGrid();
    }

    renderGrid() {
      if (!this._wrapper) return;
      this._focusTime = this._resolveFocusDate().time();
      const calEls = [];
      this._wrapper.innerHTML = '';
      for (let i = 0; i < this._options.calendars; i++) {
        const cal = this._buildMonth(this._state.viewDate.addMonths(i), i);
        this._wrapper.appendChild(cal);
        calEls.push(cal);
      }
      this._emitter._emit('render', { root: this._root, container: this._container, calendars: calEls });
    }

    /**
     * Exactly one day cell carries `tabindex="0"` (roving tabindex): the last
     * cell the user interacted with, else the start date, else today, else the
     * first cell of the view.
     */
    _resolveFocusDate() {
      const s = this._state;
      const vs = s.viewDate.startOfMonth();
      const ve = s.viewDate.addMonths(this._options.calendars).addDays(-1);
      const inView = d => !!d && !d.isBefore(vs) && !d.isAfter(ve);
      const today = CalDate.today();
      const pick = inView(s.focusDate) ? s.focusDate
        : inView(s.startDate) ? s.startDate
        : inView(today) ? today
        : vs;
      s.focusDate = pick;
      return pick;
    }

    /** Move the roving tabindex without a re-render. */
    setFocusCell(date) {
      this._state.focusDate = date;
      this._focusTime = date ? date.time() : null;
      if (!this._container) return;
      for (const el of this._container.querySelectorAll('.day')) {
        const on = !el.classList.contains('out-of-month') && +el.dataset.time === this._focusTime;
        el.setAttribute('tabindex', on ? '0' : '-1');
      }
    }

    /**
     * Earliest month the view has any reason to show: the one holding today,
     * or `minDate`'s if that is later. Everything before it is `past` or
     * `out-of-bounds` in every cell.
     */
    _navFloorMonth() {
      const today = CalDate.today().startOfMonth();
      const min = this._options.minDate ? this._options.minDate.startOfMonth() : null;
      return (min && min.isAfter(today)) ? min : today;
    }

    _canGoBack() {
      return this._state.viewDate.startOfMonth().isAfter(this._navFloorMonth());
    }

    _buildMonth(monthStart, index) {
      monthStart = monthStart.startOfMonth();
      const cal = document.createElement('div');
      cal.className = 'calendar';
      cal.dataset.calIndex = String(index);

      // A plain div, not <header>: a bare element selector inherits whatever
      // the host page declares for `header`, and <header> nested in a
      // sectioning element is not a banner landmark, so there is nothing to
      // lose semantically.
      const header = document.createElement('div');
      header.className = 'month-header';
      // Stepping back from the floor month can only reveal months that are
      // entirely unselectable, so the arrow is taken away instead of being
      // left to no-op. `disabled` stops the click; the class hides it.
      const atFloor = !this._canGoBack();
      // The arrow is `span.nav-glyph`, drawn from two rotated borders in the
      // stylesheet rather than typed as a ‹ / › character: a glyph sits inside
      // its line box wherever its font puts it, which left the chevron visibly
      // off-centre in the hover circle on some faces and fine on others.
      header.innerHTML =
        `<button type="button" class="prev${atFloor ? ' nav-disabled' : ''}"` +
        ` aria-label="Previous month"${atFloor ? ' disabled' : ''}>` +
        '<span class="nav-glyph" aria-hidden="true"></span></button>' +
        `<span class="month-title">${escapeHtml(this._monthFmt.format(monthStart.toJS()))}</span>` +
        '<button type="button" class="next" aria-label="Next month">' +
        '<span class="nav-glyph" aria-hidden="true"></span></button>';
      cal.appendChild(header);

      const daynames = document.createElement('div');
      daynames.className = 'daynames';
      daynames.innerHTML = this._dayNames.map(n => `<span>${escapeHtml(n)}</span>`).join('');
      cal.appendChild(daynames);

      const days = document.createElement('div');
      days.className = 'days';
      // `group`, not `grid`: the DOM contract keeps `.day` a direct child of
      // `.days`, so there are no row elements to carry `role="row"`.
      days.setAttribute('role', 'group');
      days.setAttribute('aria-label', this._monthFmt.format(monthStart.toJS()));
      days.innerHTML = this._monthCells(monthStart);
      cal.appendChild(days);


      return cal;
    }

    _monthCells(monthStart) {
      const firstDay = this._options.firstDay;
      const offset = (monthStart.dow() - firstDay + 7) % 7;
      const gridStart = monthStart.addDays(-offset);

      // Only as many whole weeks as this month actually needs — a fixed 42
      // cells appends a trailing row that is often entirely next-month days.
      const daysInMonth = new CalDate(monthStart.year(), monthStart.month() + 1, 0).day();
      const cellCount = Math.ceil((offset + daysInMonth) / 7) * 7;

      const ctx = this._cellCtx();
      const parts = [];
      for (let i = 0; i < cellCount; i++) {
        const d = gridStart.addDays(i);
        parts.push(this._cellHTML(d, monthStart, ctx));
      }
      return parts.join('');
    }

    _cellCtx() {
      const opt = this._options;
      const state = this._state;
      return {
        today: CalDate.today(),
        minDate: opt.minDate,
        maxDate: opt.maxDate,
        occupied: state.occupiedRanges,
        minNights: opt.minNights,
        maxNights: opt.maxNights,
        startDate: state.startDate,
        selectMode: state.selectMode,
        userFilter: opt.filter,
      };
    }

    _cellHTML(date, monthStart, ctx) {
      const classes = ['day'];
      const reason = cellReason(date, ctx);
      const inMonth = date.year() === monthStart.year() && date.month() === monthStart.month();
      if (!inMonth) classes.push('out-of-month');

      if (reason === BlockedReason.PAST) classes.push('past-day');
      if (reason === BlockedReason.OCCUPIED || reason === BlockedReason.DEPARTURE_ONLY ||
          reason === BlockedReason.MIN_STAY || reason === BlockedReason.MAX_STAY ||
          reason === BlockedReason.OUT_OF_BOUNDS) {
        classes.push('locked');
      }
      // Soft blocks are greyed but remain clickable, so no `locked`.
      if (reason === BlockedReason.SOFT) classes.push('soft');

      const { startDate, endDate, firstAvailable } = this._state;
      let isStart = false, isEnd = false, inRange = false;
      const isToday = date.isSame(ctx.today);
      if (inMonth) {
        if (isToday) classes.push('today');
        isStart = !!(startDate && date.isSame(startDate));
        isEnd = !!(endDate && date.isSame(endDate));
        // Inclusive: the edges carry the range band too, which is what makes
        // `.day.start.in-range` / `.day.end.in-range` in the CSS live.
        inRange = !!(startDate && endDate && date.isBetween(startDate, endDate));
        if (isStart) classes.push('start');
        if (isEnd) classes.push('end');
        if (inRange) classes.push('in-range');
        if (firstAvailable && date.isSame(firstAvailable)) classes.push('first-available');
      }

      const innerClasses = ['border-radius'];
      // Strike-through means "unusable in every role", so a turnover day does
      // not get it — it is grey and italic like a min-stay day, and says why.
      if (reason === BlockedReason.OCCUPIED) innerClasses.push('t-blocked');

      const attrs = [`data-time="${date.time()}"`];
      if (reason) attrs.push(`data-blocked-reason="${reason}"`);

      if (inMonth) {
        const disabled = classes.includes('locked') || classes.includes('past-day');
        attrs.push('role="button"');
        attrs.push(`aria-label="${escapeHtml(this._ariaFmt.format(date.toJS()))}"`);
        attrs.push(`aria-pressed="${isStart || isEnd || inRange}"`);
        if (isToday) attrs.push('aria-current="date"');
        if (disabled) attrs.push('aria-disabled="true"');
        attrs.push(`tabindex="${date.time() === this._focusTime ? '0' : '-1'}"`);
      } else {
        // Duplicated by the neighbouring panel and not interactive.
        attrs.push('aria-hidden="true"');
      }

      return `<div class="${classes.join(' ')}" ${attrs.join(' ')}>` +
             `<span class="${innerClasses.join(' ')}">` +
               // The number lives in its own span so it can be positioned
               // inside the circle rather than centred by it — see the
               // `.day-number` rule in KzmCal.css.
               `<span class="day-number">${date.day()}</span>` +
             `</span>` +
             `</div>`;
    }

    /**
     * The pill above the hovered cell. One element, two jobs: on a day that
     * could be picked it counts the nights so far, on a day that could not it
     * says why. The reason wins — a night count on a day you cannot choose is
     * worse than useless.
     *
     * Soft-filtered days are still clickable, so they count as selectable.
     * `past` yields empty text from the default template and so shows nothing;
     * any template returning blank hides the pill, which is the opt-out.
     */
    _updateTooltip(hoverDate) {
      const el = this._tooltipEl;
      if (!el) return;
      if (!hoverDate) {
        el.hidden = true;
        return;
      }
      const cell = this._container.querySelector(
        `.day:not(.out-of-month)[data-time="${hoverDate.time()}"]`
      );
      if (!cell) {
        el.hidden = true;
        return;
      }

      const { startDate, selectMode } = this._state;
      const reason = cellReason(hoverDate, this._cellCtx());
      const blocked = isHardReason(reason) ? reason : null;
      const nights = (!blocked && startDate && selectMode === 'to')
        ? hoverDate.diffDays(startDate)
        : 0;

      // A selectable day with no range in progress has nothing to report.
      if (!blocked && nights <= 0) {
        el.hidden = true;
        return;
      }

      const html = this._tooltipTpl({ nights, date: hoverDate, reason: blocked });
      if (!html || !String(html).trim()) {
        el.hidden = true;
        return;
      }
      el.innerHTML = html;
      el.classList.toggle('is-blocked', !!blocked);
      // Unhidden before placing: `placePill` measures the pill, and a hidden
      // element has no width.
      el.hidden = false;
      // The lift above the cell is a CSS transform, so only `top` is set here;
      // the horizontal side has to fit the pill inside the screen.
      const rect = cell.getBoundingClientRect();
      const rootRect = this._container.getBoundingClientRect();
      el.style.top = (rect.top - rootRect.top - 4) + 'px';
      placePill(el, rect, rootRect);
    }

    updateHoverPreview(hoverDate) {
      if (!this._container) return;
      const { startDate, endDate, selectMode } = this._state;
      // An already-picked end does not disable the preview: re-entering to-mode
      // (site focuses its check-out input) must preview a *replacement* end.
      const isPickingEnd = startDate && selectMode === 'to';
      const clampedEnd = (isPickingEnd && hoverDate)
        ? previewEnd(startDate, hoverDate, this._state.occupiedRanges,
                     this._options.minNights, this._options.maxNights)
        : null;

      // Not gated on isPickingEnd: blocked days explain themselves in either
      // mode, the way the standalone `popup` always did.
      this._updateTooltip(hoverDate);

      const dayEls = this._container.querySelectorAll('.day');
      for (const el of dayEls) {
        if (el.classList.contains('out-of-month')) continue;
        const t = +el.dataset.time;
        if (!t) continue;
        const d = CalDate.fromTime(t);

        const isStart = !!(startDate && d.isSame(startDate));
        let inRange = !!(startDate && endDate && d.isBetween(startDate, endDate));
        let isEnd = !!(endDate && d.isSame(endDate));

        if (clampedEnd) {
          // Replace, never union: a committed end further out than the preview
          // would otherwise leave a stale band hanging past the hovered cell.
          inRange = !d.isBefore(startDate) && !d.isAfter(clampedEnd);
          isEnd = d.isSame(clampedEnd);
        }

        el.classList.toggle('in-range', inRange);
        el.classList.toggle('end', isEnd);
        if (el.getAttribute('role') === 'button') {
          el.setAttribute('aria-pressed', String(isStart || isEnd || inRange));
        }
      }
    }
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);
  }

  /**
   * Horizontal placement for either pill — `.ep-tooltip` above the cell,
   * `.ep-popup` below it. Both are centred on the cell by a CSS
   * `translateX(-50%)`, which is enough until the cell is near the edge of the
   * screen: on a phone the calendar is one column wide, so the first and last
   * columns sit within half a pill of the edge and a wide reason string
   * ("Max. Aufenthalt 364 Nächte") hung half outside the viewport.
   *
   * So the pill is measured and pulled back inside, and the arrow — which can
   * no longer be the pill's centre — is handed its own offset in `--ep-arrow`.
   * Nothing clips the pill (`.kzm-cal` is `overflow: visible`), so the viewport
   * is the only limit worth clamping to: a desktop card narrower than the
   * screen may still overhang, exactly as it did before.
   *
   * `rect`/`rootRect` are viewport-space; `left` ends up container-relative,
   * because the pill is absolutely positioned inside `.kzm-cal`.
   */
  function placePill(el, rect, rootRect, arrowInset = 8) {
    const centerX = rect.left + rect.width / 2;
    // Measured after the text is in and while the pill is visible. A stale
    // `left` cannot skew this: `white-space: nowrap` makes the shrink-to-fit
    // width content-driven.
    const w = el.offsetWidth;
    const half = w / 2;
    const gutter = 8;
    const vw = document.documentElement?.clientWidth || window.innerWidth || 0;

    let x = centerX;
    if (vw > 0) {
      // A pill wider than the screen cannot be made to fit; centre it and let
      // the arrow clamp keep the point on the pill.
      x = (w + gutter * 2 >= vw)
        ? vw / 2
        : Math.min(Math.max(centerX, half + gutter), vw - half - gutter);
    }

    el.style.left = (x - rootRect.left) + 'px';
    // Distance from the pill's own left edge, held a corner away from either
    // end so the arrow never hangs off the rounded ends.
    const arrow = Math.min(Math.max(centerX - (x - half), arrowInset), Math.max(w - arrowInset, arrowInset));
    el.style.setProperty('--ep-arrow', arrow + 'px');
  }

  /* ========================================================================
   * PopupController — opt-in tooltip on blocked cells
   * ====================================================================== */
  class PopupController {
    constructor(root, optionOrBool, options) {
      this._root = root;
      this._tpl = (typeof optionOrBool === 'object' && optionOrBool && optionOrBool.template)
        ? optionOrBool.template
        : defaultPopupTemplate(options);
      this._popupEl = null;
      this._lastTouch = 0;
      this._hideTimer = null;
      this._onPointerOver = this._onPointerOver.bind(this);
      this._onPointerOut = this._onPointerOut.bind(this);
      this._onClickCapture = this._onClickCapture.bind(this);
      this._onTouch = this._onTouch.bind(this);

      root.addEventListener('pointerover', this._onPointerOver, true);
      root.addEventListener('pointerout', this._onPointerOut, true);
      root.addEventListener('click', this._onClickCapture, true);
      root.addEventListener('touchstart', this._onTouch, true);
    }

    destroy() {
      this._root.removeEventListener('pointerover', this._onPointerOver, true);
      this._root.removeEventListener('pointerout', this._onPointerOut, true);
      this._root.removeEventListener('click', this._onClickCapture, true);
      this._root.removeEventListener('touchstart', this._onTouch, true);
      this._hide();
    }

    _onTouch(e) {
      this._lastTouch = Date.now();
      // Touch produces no hover, and the click path below is deliberately
      // suppressed for it — so open straight from the touch, and auto-dismiss
      // since there is no pointerout to close on.
      const day = e.target.closest ? e.target.closest('.day.locked') : null;
      if (day) this._show(day, true);
      else this._hide();
    }

    _onPointerOver(e) {
      const day = e.target.closest('.day.locked');
      if (!day) return;
      this._show(day, e.pointerType === 'touch');
    }

    _onPointerOut(e) {
      // Chrome fires pointerout for a touch pointer immediately after
      // pointerup, which would close the popup the tap just opened.
      if (e.pointerType === 'touch') return;
      const day = e.target.closest('.day.locked');
      if (!day) return;
      this._hide();
    }

    _onClickCapture(e) {
      if (Date.now() - this._lastTouch < 500) return;
      const day = e.target.closest('.day.locked');
      if (!day) return;
      this._show(day);
    }

    _getEl() {
      if (!this._popupEl) {
        this._popupEl = this._root.querySelector('.ep-popup');
      }
      return this._popupEl;
    }

    _show(day, autoHide) {
      const el = this._getEl();
      if (!el) return;
      clearTimeout(this._hideTimer);
      this._hideTimer = autoHide ? setTimeout(() => this._hide(), 2500) : null;
      const reason = day.dataset.blockedReason || 'blocked';
      const t = +day.dataset.time;
      const date = t ? CalDate.fromTime(t) : null;
      el.innerHTML = this._tpl(reason, date);
      el.hidden = false;

      const rect = day.getBoundingClientRect();
      const rootRect = this._root.getBoundingClientRect();
      el.style.position = 'absolute';
      el.style.top = (rect.bottom - rootRect.top + 4) + 'px';
      // 14, not the default 8: this pill's `border-radius: 25px` would put an
      // arrow any closer to the end out on the round.
      placePill(el, rect, rootRect, 14);
    }

    _hide() {
      clearTimeout(this._hideTimer);
      this._hideTimer = null;
      const el = this._getEl();
      if (el) el.hidden = true;
    }
  }

  function defaultPopupTemplate(options) {
    const nightsWord = (n) => escapeHtml(
      (n === 1 && options.nightsLocale?.one) ? options.nightsLocale.one
                                             : (options.nightsLocale?.other || 'nights')
    );
    const n = options.minNights || 0;
    const max = options.maxNights || 0;
    const minStay = escapeHtml(options.minStayLabel);
    const maxStay = escapeHtml(options.maxStayLabel);
    const unavailable = escapeHtml(options.unavailableLabel);
    const departureOnly = escapeHtml(options.departureOnlyLabel);
    return (reason) => {
      if (reason === 'min-stay') return `${minStay} ${n} ${nightsWord(n)}`;
      if (reason === 'max-stay') return `${maxStay} ${max} ${nightsWord(max)}`;
      if (reason === 'departure-only') return departureOnly;
      if (reason === 'past') return '';
      return unavailable;
    };
  }

  /**
   * Receives `{ nights, date, reason }`. `reason` is set only for hard-blocked
   * days, in which case it takes precedence and `nights` is 0. Shares the
   * blocked wording with the standalone popup so the two never disagree.
   */
  function defaultTooltipTemplate(options) {
    const reasonTpl = defaultPopupTemplate(options);
    const loc = options.nightsLocale || {};
    const one = loc.one || 'Night';
    const other = loc.other || 'Nights';
    return ({ nights, date, reason }) => {
      if (reason) return reasonTpl(reason, date);
      return `${nights} ${escapeHtml(nights === 1 ? one : other)}`;
    };
  }

  /* ========================================================================
   * KzmCal — the main class
   * ====================================================================== */
  const DEFAULTS = {
    locale: 'en-US',
    nightsLocale: { one: 'Night', other: 'Nights' },
    firstDay: undefined,
    minNights: 0,
    maxNights: 0,
    minDate: null,
    maxDate: null,
    calendars: 2,
    containerClass: 'js-easypicker-container',
    mobilePullup: null,
    initialRange: null,
    availability: null,
    availabilityPadding: { before: 3, after: 3 },
    availabilityInitialSpan: 8,
    popup: false,
    rangeTooltip: false,
    filter: null,
    // null = "not supplied", so minNights can auto-generate one. An explicit
    // '' means the host renders its own footer and wants none from us.
    footerInfoHTML: null,
    minStayLabel: 'Min. stay',
    maxStayLabel: 'Max. stay',
    unavailableLabel: 'Unavailable',
    departureOnlyLabel: 'Departure only',
  };

  function normalizeOptions(opts) {
    if (!opts || !opts.mount || !(opts.mount instanceof HTMLElement)) {
      throw new Error('KzmCal: options.mount must be an HTMLElement');
    }
    const o = Object.assign({}, DEFAULTS, opts);
    o.nightsLocale = Object.assign({}, DEFAULTS.nightsLocale, opts.nightsLocale || {});
    o.availabilityPadding = Object.assign({}, DEFAULTS.availabilityPadding, opts.availabilityPadding || {});
    o.minDate = opts.minDate ? CalDate.fromISO(opts.minDate) : null;
    o.maxDate = opts.maxDate ? CalDate.fromISO(opts.maxDate) : null;
    o.calendars = Math.max(1, o.calendars | 0);
    o.minNights = Math.max(0, o.minNights | 0);
    // 0 = no ceiling. A max below the min would lock out every check-out, so it
    // is treated as unset rather than silently making the calendar unusable.
    o.maxNights = Math.max(0, o.maxNights | 0);
    if (o.maxNights && o.maxNights < o.minNights) o.maxNights = 0;
    if (!o.containerClass) o.containerClass = DEFAULTS.containerClass;
    if (o.minStayLabel == null) o.minStayLabel = DEFAULTS.minStayLabel;
    if (o.maxStayLabel == null) o.maxStayLabel = DEFAULTS.maxStayLabel;
    if (o.unavailableLabel == null) o.unavailableLabel = DEFAULTS.unavailableLabel;
    if (o.departureOnlyLabel == null) o.departureOnlyLabel = DEFAULTS.departureOnlyLabel;
    if (o.firstDay == null) o.firstDay = resolveFirstDay(o.locale);
    if (o.footerInfoHTML == null && o.minNights > 0) {
      const nights = o.minNights === 1 ? o.nightsLocale.one : o.nightsLocale.other;
      // `.min-nights` is what KzmCal.css turns into the circled number.
      o.footerInfoHTML = `${escapeHtml(o.minStayLabel)} ` +
        `<span class="min-nights">${o.minNights}</span> ${escapeHtml(nights)}`;
    }
    return o;
  }

  function resolveFirstDay(locale) {
    try {
      const loc = new Intl.Locale(locale);
      const wi = loc.weekInfo || (typeof loc.getWeekInfo === 'function' ? loc.getWeekInfo() : null);
      if (wi && typeof wi.firstDay === 'number') {
        return wi.firstDay === 7 ? 0 : wi.firstDay;
      }
    } catch (_) { /* fall through */ }
    return 1; // Monday fallback
  }

  class KzmCal extends Emitter {
    constructor(options) {
      super();
      const opt = normalizeOptions(options);
      this._options = opt;
      this._root = opt.mount;

      this._state = {
        startDate: null,
        endDate: null,
        viewDate: CalDate.today().startOfMonth(),
        selectMode: 'from',
        occupiedRanges: [],
        firstAvailable: null,
        focusDate: null,
      };

      // Touch bookkeeping for the hover pill — see _onPointerDown.
      this._lastTouchTs = 0;
      this._touchTipTimer = null;

      this._busy = new BusyLock(this, (busy) => {
        const c = this._renderer?.container;
        if (c) c.classList.toggle('is-loading', busy);
      });

      this._availability = new AvailabilityLoader({
        fn: opt.availability,
        busyLock: this._busy,
        emitter: this,
      });
      // Share the ranges array by reference — the loader mutates it in place.
      this._state.occupiedRanges = this._availability.ranges;

      if (opt.initialRange && opt.initialRange.from) {
        const iv = CalDate.fromISO(opt.initialRange.from);
        if (iv) this._state.viewDate = iv.startOfMonth();
      }

      this._renderer = new Renderer(this._root, opt, this._state, this);
      this._renderer.mount();
      this._installEventHandlers();

      if (opt.popup) this._popup = new PopupController(this._renderer.container, opt.popup, opt);

      /*
       * Every navigation path (_navigate, gotoDate, _moveFocus, setDateRange)
       * calls maybeExtendForVisible() *after* it has rendered, so a fetch it
       * triggers resolves into a grid nothing redraws — the user sees a
       * bookable-looking day that is in fact occupied until some unrelated
       * interaction forces a repaint. easepick handled this by calling
       * renderAll() in its ajax .then(); this is the equivalent.
       *
       * Held off until _initialFetch has done its own render, so the first
       * paint happens once, after any initialRange has been applied.
       */
      this._initialFetchDone = false;
      this.on('availability:loaded', () => {
        if (this._destroyed || !this._initialFetchDone) return;
        this._syncFirstAvailable();
        this._renderer.renderGrid();
      });

      this._initialFetchPromise = this._initialFetch();

      if (opt.mobilePullup) opt.mobilePullup('open');
    }

    /* ----- public methods ----- */

    setDateRange(fromISO, toISO) {
      const from = CalDate.fromISO(fromISO);
      const to = CalDate.fromISO(toISO);
      if (!from || !to) throw new Error('KzmCal.setDateRange: invalid ISO');
      if (!to.isAfter(from)) throw new Error('KzmCal.setDateRange: to must be after from');

      const baseCtx = {
        today: CalDate.today(),
        minDate: this._options.minDate,
        maxDate: this._options.maxDate,
        occupied: this._state.occupiedRanges,
        minNights: this._options.minNights,
        maxNights: this._options.maxNights,
        userFilter: this._options.filter,
      };
      const fromReason = cellReason(from, { ...baseCtx, startDate: null, selectMode: 'from' });
      if (isHardReason(fromReason)) {
        throw new Error(`KzmCal.setDateRange: start is blocked (${fromReason})`);
      }
      const toReason = cellReason(to, { ...baseCtx, startDate: from, selectMode: 'to' });
      if (isHardReason(toReason)) {
        throw new Error(`KzmCal.setDateRange: end is blocked (${toReason})`);
      }

      this._state.startDate = from;
      this._state.endDate = to;
      this._state.selectMode = 'from';
      this._syncFirstAvailable();
      this._ensureWindowCoversDate(from);
      this._renderer.renderGrid();
      this._emit('select', {
        start: from.toISOLocal(),
        end: to.toISOLocal(),
        nights: to.diffDays(from),
      });
      this._availability.maybeExtendForVisible(this._visibleStart(), this._visibleEnd());
    }

    setMode(mode) {
      if (mode !== 'from' && mode !== 'to') return;
      if (this._state.selectMode === mode) return;
      this._state.selectMode = mode;
      // Entering to-mode from the site's check-out input must behave like
      // picking a start in-grid. The committed end is kept — hovering previews
      // over it, leaving the grid restores it.
      this._syncFirstAvailable();
      this._renderer.renderGrid();
      this._emit('modechange', { mode });
    }

    clear() {
      const modeChanged = this._state.selectMode !== 'from';
      this._state.startDate = null;
      this._state.endDate = null;
      this._state.selectMode = 'from';
      this._syncFirstAvailable();
      this._renderer.renderGrid();
      this._emit('clear', { what: 'all' });
      if (modeChanged) this._emit('modechange', { mode: 'from' });
    }

    clearEnd() {
      const modeChanged = this._state.selectMode !== 'to';
      this._state.endDate = null;
      this._state.selectMode = 'to';
      this._syncFirstAvailable();
      this._renderer.renderGrid();
      this._emit('clear', { what: 'end' });
      if (modeChanged) this._emit('modechange', { mode: 'to' });
    }

    gotoDate(dateISO) {
      const d = CalDate.fromISO(dateISO);
      if (!d) return;
      this._state.viewDate = d.startOfMonth();
      this._renderer.renderGrid();
      this._availability.maybeExtendForVisible(this._visibleStart(), this._visibleEnd());
    }

    // The availability:loaded listener installed in the constructor does the
    // redraw, including re-deriving first-available — a booking made by someone
    // else can move or retire the ring.
    refreshAvailability() {
      return this._availability.refresh();
    }

    holdBusy(pOrFn, key) { return this._busy.holdBusy(pOrFn, key); }
    beginBusy(key) { this._busy.beginBusy(key); }
    endBusy(key) { this._busy.endBusy(key); }

    getStartDate() { return this._state.startDate ? this._state.startDate.toISOLocal() : null; }
    getEndDate() { return this._state.endDate ? this._state.endDate.toISOLocal() : null; }
    getDateRange() {
      return {
        start: this._state.startDate ? this._state.startDate.toISOLocal() : null,
        end: this._state.endDate ? this._state.endDate.toISOLocal() : null,
      };
    }

    destroy() {
      if (this._options.mobilePullup) {
        try { this._options.mobilePullup('close'); } catch (_) {}
      }
      if (this._popup) this._popup.destroy();
      this._teardownEventHandlers();
      this._root.innerHTML = '';
      this._listeners.clear();
      this._destroyed = true;
    }

    /* ----- internals ----- */

    _installEventHandlers() {
      const c = this._renderer.container;
      if (!c) return;
      this._onClick = this._onClick.bind(this);
      this._onPointerOver = this._onPointerOver.bind(this);
      this._onPointerLeave = this._onPointerLeave.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      c.addEventListener('click', this._onClick);
      c.addEventListener('pointerover', this._onPointerOver);
      c.addEventListener('pointerleave', this._onPointerLeave);
      c.addEventListener('pointerdown', this._onPointerDown);
      c.addEventListener('keydown', this._onKeyDown);
    }

    _teardownEventHandlers() {
      if (this._touchTipTimer) {
        clearTimeout(this._touchTipTimer);
        this._touchTipTimer = null;
      }
      const c = this._renderer?.container;
      if (!c) return;
      c.removeEventListener('click', this._onClick);
      c.removeEventListener('pointerover', this._onPointerOver);
      c.removeEventListener('pointerleave', this._onPointerLeave);
      c.removeEventListener('pointerdown', this._onPointerDown);
      c.removeEventListener('keydown', this._onKeyDown);
    }

    _onClick(e) {
      if (e.target.closest('.prev')) { this._navigate(-1); return; }
      if (e.target.closest('.next')) { this._navigate(+1); return; }
      const dayEl = e.target.closest('.day');
      if (!dayEl) return;
      if (dayEl.classList.contains('locked') || dayEl.classList.contains('past-day')) return;
      const t = +dayEl.dataset.time;
      if (!t) return;
      this._handleDayClick(CalDate.fromTime(t));
    }

    _onPointerOver(e) {
      if (this._afterTouch()) return;
      const dayEl = e.target.closest('.day');
      if (!dayEl) {
        // Out-of-month cells have `pointer-events: none`, so the grid itself is
        // the target there — keep the preview. Anywhere else (header, day
        // names, footer) the pointer has left the days, so drop it.
        if (!e.target.closest('.days')) this._renderer.updateHoverPreview(null);
        return;
      }
      const t = +dayEl.dataset.time;
      if (!t) return;
      this._renderer.updateHoverPreview(CalDate.fromTime(t));
    }

    _onPointerLeave() {
      if (this._afterTouch()) return;
      this._renderer.updateHoverPreview(null);
    }

    /*
     * A touch produces a synthetic pointerover/pointerout pair around the tap,
     * which would open and immediately close the pill. Anything within this
     * window of a real touch is that echo and is ignored — the same 350 ms
     * guard the easepick popup used.
     */
    _afterTouch() {
      return Date.now() - this._lastTouchTs < 350;
    }

    /**
     * Touch path for the hover pill. Hovering does not exist on a phone, and on
     * a property page most days are blocked, so a tap has to be able to say
     * why. `_updateTooltip` already decides what a cell has to report — reason
     * when blocked, night count while a check-out is being picked, nothing
     * otherwise — so this only has to open it and set it to expire.
     */
    _onPointerDown(e) {
      if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
      this._lastTouchTs = Date.now();
      if (!this._options.rangeTooltip) return;
      const dayEl = e.target.closest('.day');
      if (!dayEl) return;
      const t = +dayEl.dataset.time;
      if (!t) return;
      this._renderer.updateHoverPreview(CalDate.fromTime(t));
      if (this._touchTipTimer) clearTimeout(this._touchTipTimer);
      this._touchTipTimer = setTimeout(() => {
        this._touchTipTimer = null;
        if (this._destroyed) return;
        this._renderer.updateHoverPreview(null);
      }, 1000);
    }

    _onKeyDown(e) {
      if (!e.target.closest) return;
      const dayEl = e.target.closest('.day');
      if (!dayEl) return;
      const t = +dayEl.dataset.time;
      if (!t) return;
      const cur = CalDate.fromTime(t);
      const firstDay = this._options.firstDay;
      let next = null;

      switch (e.key) {
        case 'ArrowLeft':  next = cur.addDays(-1); break;
        case 'ArrowRight': next = cur.addDays(1); break;
        case 'ArrowUp':    next = cur.addDays(-7); break;
        case 'ArrowDown':  next = cur.addDays(7); break;
        case 'Home':       next = cur.addDays(-((cur.dow() - firstDay + 7) % 7)); break;
        case 'End':        next = cur.addDays(6 - ((cur.dow() - firstDay + 7) % 7)); break;
        case 'PageUp':     next = cur.addMonths(e.shiftKey ? -12 : -1); break;
        case 'PageDown':   next = cur.addMonths(e.shiftKey ? 12 : 1); break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!dayEl.classList.contains('locked') && !dayEl.classList.contains('past-day')) {
            this._handleDayClick(cur);
          }
          this._focusDay(cur);
          return;
        default:
          return;
      }

      e.preventDefault();
      this._moveFocus(next);
    }

    _moveFocus(date) {
      const cals = this._options.calendars;
      if (date.isBefore(this._visibleStart())) {
        this._state.focusDate = date;
        this._state.viewDate = date.startOfMonth();
        this._renderer.renderGrid();
        this._availability.maybeExtendForVisible(this._visibleStart(), this._visibleEnd());
      } else if (date.isAfter(this._visibleEnd())) {
        this._state.focusDate = date;
        this._state.viewDate = date.startOfMonth().addMonths(-(cals - 1));
        this._renderer.renderGrid();
        this._availability.maybeExtendForVisible(this._visibleStart(), this._visibleEnd());
      } else {
        this._renderer.setFocusCell(date);
      }
      this._focusDay(date);
    }

    _focusDay(date) {
      const c = this._renderer.container;
      if (!c) return;
      const el = c.querySelector(`.day:not(.out-of-month)[data-time="${date.time()}"]`);
      if (el) el.focus();
    }

    _navigate(delta) {
      // The arrow is hidden and disabled at the floor, but a synthetic click
      // still reaches this handler, so the floor is enforced here too.
      // gotoDate() and keyboard paging are deliberately not bound by it.
      if (delta < 0 && !this._renderer._canGoBack()) return;
      this._state.viewDate = this._state.viewDate.addMonths(delta);
      this._renderer.renderGrid();
      this._availability.maybeExtendForVisible(this._visibleStart(), this._visibleEnd());
    }

    _handleDayClick(date) {
      const opt = this._options;
      const { startDate, selectMode } = this._state;
      this._state.focusDate = date;

      const ctx = {
        today: CalDate.today(),
        minDate: opt.minDate,
        maxDate: opt.maxDate,
        occupied: this._state.occupiedRanges,
        minNights: opt.minNights,
        startDate: selectMode === 'to' ? startDate : null,
        selectMode,
        userFilter: opt.filter,
      };
      const reason = cellReason(date, ctx);
      if (isHardReason(reason)) return;

      if (selectMode === 'from' || !startDate) {
        // A check-out the user never touched survives a re-pick of the check-in,
        // provided it is still legal against the new one: still after it, still
        // clear of min-stay, and with no block opening up in between. Anything
        // else falls back to dropping it and asking for a fresh check-out.
        const prevEnd = this._state.endDate;
        const keepEnd = !!prevEnd && prevEnd.isAfter(date) &&
          !isHardReason(cellReason(prevEnd, { ...ctx, startDate: date, selectMode: 'to' }));

        this._state.startDate = date;
        this._state.endDate = keepEnd ? prevEnd : null;
        this._state.selectMode = keepEnd ? 'from' : 'to';
        this._syncFirstAvailable();
        this._renderer.renderGrid();

        if (keepEnd) {
          // A complete range, so this is a `select`, not a `preselect` — the
          // site must not blank its check-out field.
          this._emit('select', {
            start: date.toISOLocal(),
            end: prevEnd.toISOLocal(),
            nights: prevEnd.diffDays(date),
          });
          return;
        }
        this._emit('preselect', { start: date.toISOLocal() });
        if (selectMode !== 'to') this._emit('modechange', { mode: 'to' });
        return;
      }

      if (!date.isAfter(startDate)) {
        // clicking before or on start in to-mode: treat as new start pick
        this._state.startDate = date;
        this._state.endDate = null;
        this._state.selectMode = 'to';
        this._syncFirstAvailable();
        this._renderer.renderGrid();
        this._emit('preselect', { start: date.toISOLocal() });
        return;
      }

      // valid end
      this._state.endDate = date;
      this._state.selectMode = 'from';
      this._syncFirstAvailable();
      this._renderer.renderGrid();
      this._emit('select', {
        start: startDate.toISOLocal(),
        end: date.toISOLocal(),
        nights: date.diffDays(startDate),
      });
      this._emit('modechange', { mode: 'from' });
    }

    /**
     * `.first-available` is a pure function of the check-in: it marks the
     * earliest legal check-out and so is meaningful for as long as a check-in
     * exists — mode and an already-picked check-out are irrelevant. Derived in
     * one place and called before every render, so it cannot drift out of sync
     * with startDate, minNights, or newly-loaded availability.
     */
    _syncFirstAvailable() {
      const s = this._state;
      s.firstAvailable = s.startDate
        ? firstAvailableEnd(s.startDate, this._options.minNights, s.occupiedRanges, this._options.maxDate)
        : null;
    }

    _visibleStart() { return this._state.viewDate.startOfMonth(); }
    _visibleEnd() {
      return this._state.viewDate.addMonths(this._options.calendars).addDays(-1);
    }

    _ensureWindowCoversDate(date) {
      // shift viewDate so that `date` is visible in the first calendar
      const vs = this._visibleStart();
      const ve = this._visibleEnd();
      if (date.isBefore(vs) || date.isAfter(ve)) {
        this._state.viewDate = date.startOfMonth();
      }
    }

    async _initialFetch() {
      const opt = this._options;
      let from, to;
      if (opt.initialRange && opt.initialRange.from && opt.initialRange.to) {
        from = CalDate.fromISO(opt.initialRange.from).addMonths(-opt.availabilityPadding.before);
        to = CalDate.fromISO(opt.initialRange.to).addMonths(opt.availabilityPadding.after);
      } else {
        from = CalDate.today();
        to = CalDate.today().addMonths(opt.availabilityInitialSpan);
      }
      await this._availability.fetch(from, to);
      if (this._destroyed) return;
      this._renderer.renderGrid();
      if (opt.initialRange && opt.initialRange.from && opt.initialRange.to) {
        try {
          this.setDateRange(opt.initialRange.from, opt.initialRange.to);
        } catch (e) {
          /*
           * A server-rendered preselection can go stale between page render and
           * page load — kurzzeitmiete.at's property page preselects
           * "tomorrow + 31 nights" without consulting availability at all, so
           * the range routinely lands inside a booking. Warning to the console
           * and opening empty leaves the host with no way to tell the user why
           * their dates vanished, so this is also an event.
           */
          console.warn('KzmCal: initialRange is not currently valid —', e.message);
          this._emit('initialrange:rejected', {
            from: opt.initialRange.from,
            to: opt.initialRange.to,
            reason: e.message,
          });
        }
      }
      this._initialFetchDone = true;
    }
  }

  /* ========================================================================
   * jQuery adapter (optional)
   * ====================================================================== */
  function installJQueryAdapter($) {
    if (!$ || !$.fn || $.fn.kzmCal) return;
    $.fn.kzmCal = function (optionsOrMethod, ...args) {
      let result;
      this.each(function () {
        const existing = $(this).data('kzmCal');
        if (typeof optionsOrMethod === 'string') {
          if (!existing) return;
          if (optionsOrMethod === 'destroy') {
            existing.destroy();
            $(this).removeData('kzmCal');
            return;
          }
          if (typeof existing[optionsOrMethod] === 'function') {
            const r = existing[optionsOrMethod](...args);
            if (result === undefined) result = r;
          }
          return;
        }
        const opts = Object.assign({}, optionsOrMethod || {}, { mount: this });
        const inst = new KzmCal(opts);
        $(this).data('kzmCal', inst);
      });
      return result !== undefined ? result : this;
    };
  }

  if (global.jQuery) installJQueryAdapter(global.jQuery);
  // Also expose the installer for late-loaded jQuery
  KzmCal.installJQueryAdapter = installJQueryAdapter;
  KzmCal.CalDate = CalDate;

  global.KzmCal = KzmCal;
})(typeof window !== 'undefined' ? window : globalThis);
